import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNumber } from 'lodash';
import {
    DexDispatcherFormEnumType,
    DexDispatcherFormEnum,
    EventPanelMetadata,
    FirearmInquiry,
    PersonInquiry,
    SingleArticleInquiry,
    VehicleRegistrationInquiry,
} from '@mark43/cad-api';
import { QueryableEntityType } from 'mark43-federated-search';
import {
    Person,
    Vehicle,
    Entity,
    EntityTypes,
    MergedEntityTypes,
    Property,
    CorrectedFirearm,
    CorrectedOtherItem,
} from '../../../../fr-entity-panels/types';
import { useEventMetadataStore } from '../../../../event-panel/event-panel-form/event-metadata';
import { PERSON_ENTITY_TYPES, PROPERTY_ENTITY_TYPES } from '../../constants';
import { getPersonYearOfBirth, getPersonSexNcicCode } from './helpers';
import { useDexResults } from '../../../../data-exchange/results/use-dex-results';
import { FR_DEX_WINDOW_PATH } from '../../../../router/constants';
import { EventWorkspacePanelDexNextFormPrefill } from '../../../../panels/workspace-panel-types';

export function useGoToDexPageWithEntityPrefilled({
    entity,
    entityType,
}: {
    entity: Entity;
    entityType: EntityTypes | MergedEntityTypes;
}) {
    const { agencyEventNumber } =
        useEventMetadataStore()?.state?.metadata ?? ({} as EventPanelMetadata);
    const { dexResultsStore } = useDexResults();
    const navigate = useNavigate();
    const isPersonCard = PERSON_ENTITY_TYPES.includes(entityType);
    const isPropertyCard = PROPERTY_ENTITY_TYPES.includes(entityType);
    const isFirearm =
        entityType === EntityTypes.FIREARMS || entityType === MergedEntityTypes.FIREARMS;
    const isOtherItem =
        entityType === EntityTypes.OTHER_ITEMS || entityType === MergedEntityTypes.OTHER_ITEMS;

    return useCallback(() => {
        let personPrefillValues: PersonInquiry | undefined;
        let vehiclePrefillValues: VehicleRegistrationInquiry | undefined;
        let firearmPrefillValues: FirearmInquiry | undefined;
        let articlePrefillValues: SingleArticleInquiry | undefined;
        let initialFrFormType: DexDispatcherFormEnumType | undefined;
        let dexNextPrefillValues: EventWorkspacePanelDexNextFormPrefill | undefined;

        if (isPersonCard) {
            initialFrFormType = DexDispatcherFormEnum.PERSON_MASTER_QUERY.name;
            const person = entity as Person;
            const birthYear = getPersonYearOfBirth(person?.dateOfBirth);
            personPrefillValues = {
                nameFirst: person?.firstName ?? undefined,
                nameLast: person?.lastName ?? undefined,
                nameMiddle: person?.middleName ?? undefined,
                operatorLicenseNumber: person?.driversLicenseNumber ?? undefined,
                sexCode: getPersonSexNcicCode(person?.sex?.label),
                state: person?.driversLicenseState?.label,
                birthDate: person?.dateOfBirth ? person.dateOfBirth.split('-').join('') : undefined,
                yearOfBirth: birthYear ? String(birthYear) : undefined,
                address: person?.location
                    ? `${person.location.displayLineFirst} ${person.location.displayLineLast}`
                    : undefined,
                addressState: person?.location?.adminArea1 ?? undefined,
                addressZipCode: person?.location?.postalCode ?? undefined,
            };
            dexNextPrefillValues = {
                entityType: QueryableEntityType.PERSON,
                prefillValues: {
                    NameFirst: person?.firstName ?? undefined,
                    NameLast: person?.lastName ?? undefined,
                    NameMiddle: person?.middleName ?? undefined,
                    BirthDate: person?.dateOfBirth ?? undefined,
                    Height: person?.height ?? undefined,
                    OperatorLicenseNumber: person?.driversLicenseNumber ?? undefined,
                    NamePhoneNumbers: person?.phoneNumber
                        ? [{ phoneNumber: person?.phoneNumber }]
                        : undefined,
                    RegistrationState: person?.driversLicenseState?.value
                        ? String(person?.driversLicenseState?.value)
                        : undefined,
                    RaceCode: person?.race?.value ? String(person?.race?.value) : undefined,
                    SexCode: person?.sex?.value ? String(person?.sex?.value) : undefined,
                    EyeColorCode: person?.eyeColor ? String(person?.eyeColor) : undefined,
                    HairColorCode: person?.hairColor ? String(person?.hairColor) : undefined,
                    FreeText: person?.notes ?? undefined,
                },
            };
        } else if (isPropertyCard) {
            let property = entity as Property;
            if (isFirearm) {
                initialFrFormType = DexDispatcherFormEnum.FIREARM_MASTER_QUERY.name;
                property = entity as CorrectedFirearm;
                firearmPrefillValues = {
                    gunSerialNumber: property.serialNumber ?? undefined,
                    gunMake: property.firearmMake?.label ?? undefined,
                    gunCaliber: property.caliber?.label ?? undefined,
                };
                dexNextPrefillValues = {
                    entityType: QueryableEntityType.FIREARM,
                    prefillValues: {
                        SerialNumber: property?.serialNumber ?? undefined,
                        FirearmMake: property?.firearmMake?.value
                            ? String(property?.firearmMake?.value)
                            : undefined,
                        Caliber: property?.caliber?.value
                            ? String(property?.caliber?.value)
                            : undefined,
                        GunType: property?.firearmType?.value
                            ? String(property?.firearmType?.value)
                            : undefined,
                        FreeText: property?.description ?? undefined,
                    },
                };
            } else if (isOtherItem) {
                initialFrFormType = DexDispatcherFormEnum.ARTICLE_MASTER_QUERY.name;
                property = entity as CorrectedOtherItem;
                articlePrefillValues = {
                    articleSerialNumber: property.serialNumber ?? undefined,
                    articleTypeCode: property?.itemCategory?.label ?? undefined,
                };
                dexNextPrefillValues = {
                    entityType: QueryableEntityType.ARTICLE,
                    prefillValues: {
                        SerialNumber: property?.serialNumber ?? undefined,
                        ArticleCategory: property?.itemCategory?.value
                            ? String(property?.itemCategory?.value)
                            : undefined,
                        ArticleModel: property?.model ?? undefined,
                        FreeText: property?.description ?? undefined,
                    },
                };
            }
        } else {
            initialFrFormType = DexDispatcherFormEnum.VEHICLE_MASTER_QUERY.name;
            const vehicle = entity as Vehicle;
            vehiclePrefillValues = {
                licensePlateNumber: vehicle?.plateNumber ?? undefined,
                licensePlateYear: vehicle?.year ? String(vehicle.year) : undefined,
                state: vehicle?.stateOption?.label ?? undefined,
                vehicleIdentificationNumber: vehicle?.vinNumber ?? undefined,
                vehicleYear: vehicle?.year ? String(vehicle.year) : undefined,
                vehicleMake: vehicle?.vehicleMakeOption?.label ?? undefined,
                vehicleMakeCode: vehicle?.vehicleMakeOption?.ncicCode ?? undefined,
            };
            dexNextPrefillValues = {
                entityType: QueryableEntityType.VEHICLE,
                prefillValues: {
                    VehicleIdentificationNumber: vehicle?.vinNumber ?? undefined,
                    LicensePlateNumber: vehicle?.plateNumber ?? undefined,
                    VehicleYear: isNumber(vehicle?.year) ? `${vehicle?.year}` : undefined,
                    RegistrationState: vehicle?.stateOption?.value
                        ? String(vehicle?.stateOption?.value)
                        : undefined,
                    VehicleMakeCode: vehicle?.vehicleMakeOption?.ncicCode,
                    VehicleMakeName: vehicle?.vehicleMakeOption?.value
                        ? String(vehicle?.vehicleMakeOption?.value)
                        : undefined,
                    VehicleColorCode: vehicle?.colorOption?.value
                        ? String(vehicle?.colorOption?.value)
                        : undefined,
                    VehicleModelName: vehicle?.vehicleModelOption?.value
                        ? String(vehicle?.vehicleModelOption?.value)
                        : undefined,
                    VehicleModelCode: vehicle?.vehicleModelOption?.ncicCode,
                    FreeText: vehicle?.notes ?? undefined,
                },
            };
        }

        dexResultsStore.setFrDexPageViewMode('NEW_FORM');
        dexResultsStore.setInitialFrFormType(initialFrFormType);
        dexResultsStore.setFrPrefillValues({
            eventNumber: agencyEventNumber,
            person: personPrefillValues,
            vehicle: vehiclePrefillValues,
            firearm: firearmPrefillValues,
            article: articlePrefillValues,
        });
        dexResultsStore.setFrNextPrefillValues(dexNextPrefillValues);

        navigate(FR_DEX_WINDOW_PATH);
    }, [
        dexResultsStore,
        entity,
        isPersonCard,
        isPropertyCard,
        isFirearm,
        isOtherItem,
        agencyEventNumber,
        navigate,
    ]);
}
